import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createTwibbonClient } from "@fotofit/twibbon";
import { Download, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  createHaafReceiptRenderSession,
  getHaafReceiptAssetBlob,
} from "@/api/haaf-payments/api";

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADElEQVR42mP8z8BQDwAFgwJ/lWk0UgAAAABJRU5ErkJggg==";

function mapReceiptFields(campaign, frameId, receiptFields) {
  const frame = campaign?.frames?.find((item) => item.frameId === frameId);
  const fields = {};

  frame?.objects
    ?.filter((object) => object.type === "text")
    .forEach((object) => {
      const key = object.text;
      if (Object.prototype.hasOwnProperty.call(receiptFields, key)) {
        fields[object.id] = receiptFields[key];
      }
    });

  return fields;
}

async function proxySelectedFrameImage(campaign, frameId) {
  const frame = campaign?.frames?.find((item) => item.frameId === frameId);
  if (!frame?.mainFrameImage || frame.mainFrameImage.startsWith("blob:")) {
    return null;
  }

  const blob = await getHaafReceiptAssetBlob(frame.mainFrameImage);
  const blobUrl = URL.createObjectURL(blob);
  frame.mainFrameImage = blobUrl;
  return blobUrl;
}

export function HaafReceiptModal({ isOpen, onClose, payment }) {
  const [showCurrency, setShowCurrency] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [renderResult, setRenderResult] = useState(null);
  const [renderError, setRenderError] = useState("");
  const [isRendering, setIsRendering] = useState(false);
  const renderContextRef = useRef(null);
  const previewUrlRef = useRef("");
  const frameBlobUrlsRef = useRef(new Map());

  const variant = showCurrency ? "currency" : "standard";

  const clearPreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    setPreviewUrl("");
  }, []);

  const clearFrameBlobUrls = useCallback(() => {
    frameBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    frameBlobUrlsRef.current.clear();
  }, []);

  const resetRenderResources = useCallback(() => {
    clearPreviewUrl();
    clearFrameBlobUrls();
    renderContextRef.current = null;
  }, [clearFrameBlobUrls, clearPreviewUrl]);

  const ensureRenderContext = useCallback(async () => {
    if (renderContextRef.current) {
      return renderContextRef.current;
    }

    const session = await createHaafReceiptRenderSession({ payment });
    const client = createTwibbonClient({
      campaignId: session.campaignId,
      token: session.browserToken,
      apiBaseUrl: session.apiBaseUrl,
    });
    const campaign = await client.load();
    const context = { session, client, campaign };
    renderContextRef.current = context;
    return context;
  }, [payment]);

  const ensureProxiedFrameImage = useCallback(async (campaign, frameId) => {
    const cachedBlobUrl = frameBlobUrlsRef.current.get(frameId);
    const frame = campaign?.frames?.find((item) => item.frameId === frameId);
    if (cachedBlobUrl) {
      if (frame) frame.mainFrameImage = cachedBlobUrl;
      return;
    }

    const blobUrl = await proxySelectedFrameImage(campaign, frameId);
    if (blobUrl) {
      frameBlobUrlsRef.current.set(frameId, blobUrl);
    }
  }, []);

  const renderReceiptImage = useCallback(
    async (selectedVariant) => {
      const { session, client, campaign } = await ensureRenderContext();
      const frameConfig = session.frames?.[selectedVariant] || {
        frameId: session.frameId,
        fields: session.fields,
      };

      if (!frameConfig?.frameId) {
        throw new Error("Receipt frame configuration is missing.");
      }

      await ensureProxiedFrameImage(campaign, frameConfig.frameId);

      return client.render({
        photo: TRANSPARENT_PIXEL,
        frameId: frameConfig.frameId,
        fields: mapReceiptFields(campaign, frameConfig.frameId, frameConfig.fields),
        filename: `haf-payment-receipt-${payment.paymentId}.png`,
      });
    },
    [ensureProxiedFrameImage, ensureRenderContext, payment],
  );

  useEffect(() => {
    if (!isOpen || !payment) {
      resetRenderResources();
      setRenderResult(null);
      setRenderError("");
      setIsRendering(false);
      return undefined;
    }

    return () => {
      resetRenderResources();
    };
  }, [isOpen, payment, resetRenderResources]);

  useEffect(() => {
    if (!isOpen || !payment) {
      return undefined;
    }

    let isCurrent = true;
    let nextPreviewUrl = "";

    async function renderReceipt() {
      try {
        setIsRendering(true);
        setRenderError("");
        clearPreviewUrl();
        setRenderResult(null);

        const result = await renderReceiptImage(variant);
        nextPreviewUrl = URL.createObjectURL(result.blob);

        if (isCurrent) {
          setRenderResult(result);
          previewUrlRef.current = nextPreviewUrl;
          setPreviewUrl(nextPreviewUrl);
          nextPreviewUrl = "";
        }
      } catch (error) {
        console.error("Error rendering HAF receipt:", error);
        if (isCurrent) {
          setRenderError(
            error.response?.data?.message ||
              error.message ||
              "Failed to render receipt. Please try again.",
          );
        }
      } finally {
        if (isCurrent) {
          setIsRendering(false);
        }
        if (nextPreviewUrl) {
          URL.revokeObjectURL(nextPreviewUrl);
        }
      }
    }

    renderReceipt();

    return () => {
      isCurrent = false;
    };
  }, [clearPreviewUrl, isOpen, payment, renderReceiptImage, variant]);

  if (!payment) return null;

  const handleDownload = () => {
    renderResult?.download();
  };

  const handleRetry = async () => {
    resetRenderResources();
    setRenderResult(null);
    setRenderError("");
    setIsRendering(true);
    let nextPreviewUrl = "";

    try {
      const result = await renderReceiptImage(variant);
      nextPreviewUrl = URL.createObjectURL(result.blob);
      setRenderResult(result);
      previewUrlRef.current = nextPreviewUrl;
      setPreviewUrl(nextPreviewUrl);
      nextPreviewUrl = "";
    } catch (error) {
      console.error("Error rendering HAF receipt:", error);
      setRenderError(
        error.response?.data?.message ||
          error.message ||
          "Failed to render receipt. Please try again.",
      );
    } finally {
      setIsRendering(false);
      if (nextPreviewUrl) {
        URL.revokeObjectURL(nextPreviewUrl);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[95vw] max-h-[95vh] overflow-y-auto p-4 md:p-6"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold">HAF Payment Receipt</h3>
            <p className="text-sm text-muted-foreground">
              {payment.currency} {payment.amount}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Standard</span>
            <button
              type="button"
              onClick={() => setShowCurrency((value) => !value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                showCurrency ? "bg-blue-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showCurrency ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-sm font-medium">Currency</span>
          </div>

          <div className="flex min-h-[420px] w-full items-center justify-center rounded-md border bg-muted/30 p-3 sm:min-h-[640px]">
            {isRendering ? (
              <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                Rendering receipt...
              </div>
            ) : renderError ? (
              <div className="flex max-w-sm flex-col items-center gap-3 text-center">
                <p className="text-sm text-destructive">{renderError}</p>
                <Button type="button" variant="outline" onClick={handleRetry}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt="HAF payment receipt"
                className="max-h-[75vh] max-w-full rounded-sm object-contain shadow-sm"
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Receipt not available
              </p>
            )}
          </div>

          <Button
            onClick={handleDownload}
            disabled={!renderResult || isRendering}
          >
            <Download className="mr-2 h-4 w-4" />
            Download Receipt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

HaafReceiptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  payment: PropTypes.shape({
    paymentId: PropTypes.string.isRequired,
    payee: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
    chapter: PropTypes.shape({
      chapterName: PropTypes.string,
    }),
    batch: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    paymentDate: PropTypes.string,
  }),
};
